import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * The foodservice equipment entitlement (admin-foodservice-entitlement).
 *
 * Chain accounts run equipment requisition/purchasing/install in the
 * foodservice portal; independents buy equipment through ERLS and never get
 * this. See docs/HANDOFF-FOODSERVICE-ERLS-SEAM.md.
 */

export interface FoodserviceChainContext {
  role: 'master' | 'member' | 'none';
  brandQboId: number | null;
  masterName: string | null;
  groupKind: 'chain' | 'franchise' | null;
}

export interface FoodservicePortalTenant {
  id: string;
  name: string;
  slug: string | null;
  customer_kind: string | null;
  qbo_customer_id: string | number | null;
  active?: boolean;
}

export interface FoodserviceState {
  ok: true;
  /** PROVISION_SECRET present on this site — without it nothing can be granted. */
  configured: boolean;
  enabled: boolean;
  provisioned_at: string | null;
  tenant_id: string | null;
  note: string | null;
  chain: FoodserviceChainContext;
  /** Live read from the portal — authoritative over our own flag. */
  portal_tenant: FoodservicePortalTenant | null;
  probe_error: string | null;
}

export interface FoodserviceGrantResult {
  ok: true;
  enabled: boolean;
  created?: boolean;
  tenant?: FoodservicePortalTenant;
  stores?: { created?: number; skipped?: number } | null;
  portal_url?: string | null;
  next_steps?: string[];
  note?: string;
  message?: string;
}

export function useFoodserviceEntitlement(customerId: string) {
  return useQuery({
    queryKey: ['admin', 'foodservice', customerId],
    queryFn: () =>
      adminFetch<FoodserviceState>(
        `admin-foodservice-entitlement?customer_id=${encodeURIComponent(customerId)}`,
      ),
    staleTime: 30_000,
  });
}

export function useSetFoodserviceEntitlement(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      adminFetch<FoodserviceGrantResult>('admin-foodservice-entitlement', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, value }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'foodservice', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'customer', customerId] });
    },
  });
}
